import { useState } from 'react';
import { Link, useLoaderData, useLocation, useNavigate, useRevalidator } from 'react-router';

import { Button, Card, Icon, KpiCard } from '@ds/core';
import { DataTable } from '@ds/data';
import { Alert } from '@ds/feedback';
import { Tabs } from '@ds/navigation';
import { PageHeader } from '@/comuns/PageHeader';
import { dataSimples, moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { useConfirmacao } from '@/comuns/useConfirmacao';
import { pode } from '@/session/sessao';
import type { CargaDoFaturamento, Fatura } from '@/data/tipos';
import { rotaPorId } from '@/router/rotas';
import { colunasDeFatura, useCobrancaDasFaturas } from './CobrancaDaClinica';
import { EstornoModal, FaturaAvulsaModal } from './ModaisDeFatura';
import { PainelDeNotasFiscais } from '@/screens/notasFiscais/PainelDeNotasFiscais';

function competenciaAtual(): string {
    return new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }).slice(0, 7);
}

export function Faturamento() {
    const carga = useLoaderData() as CargaDoFaturamento;
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const aviso = useAviso();
    const rota = rotaPorId('faturamento');

    const ultimo = useLocation().pathname.split('/').pop() ?? '';
    const aba = (rota?.children ?? []).some((c) => c.path === ultimo) ? ultimo : 'resumo';

    const [competencia, setCompetencia] = useState(competenciaAtual());
    const [gerando, setGerando] = useState(false);
    const [avulsa, setAvulsa] = useState(false);
    const [estornando, setEstornando] = useState<Fatura | null>(null);

    const [cobrando, setCobrando] = useState(false);

    /* As faturas em aberto sem cobrança ganham uma no gateway ativo. Sem gateway
       ativo, depois de gerar não há o que dizer: a cobrança segue manual, como a
       descrição da tela já conta; pedida no botão, a tela diz por que não cobrou. */
    const cobrarPendentes = async (pedido: boolean) => {
        setCobrando(true);
        try {
            const r = await dados().criarCobrancasPendentes();
            if (r.semGateway) {
                if (pedido) aviso({ title: 'Nenhum gateway ativo', description: 'Ative um em Integrações para cobrar online; sem ele, a baixa é registrada aqui.', tone: 'info' });
                return;
            }
            const partes = [
                r.falhas.length ? `${r.falhas.length} não saíram — o motivo aparece na fatura` : '',
                r.restantes ? `${r.restantes} ficaram para depois: use "Cobrar pendentes"` : '',
            ].filter(Boolean);
            aviso({
                title: r.criadas ? `${r.criadas} ${r.criadas === 1 ? 'cobrança criada' : 'cobranças criadas'}` : 'Nenhuma cobrança criada',
                description: partes.length ? partes.join(' · ') : r.criadas ? 'o link de pagamento já está em cada fatura' : 'não havia fatura em aberto sem cobrança',
                tone: r.falhas.length ? 'danger' : r.criadas ? 'success' : 'info',
            });
        } catch (e) {
            aviso({ title: 'As cobranças não foram criadas', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setCobrando(false);
            revalidator.revalidate();
        }
    };

    const gerar = async () => {
        setGerando(true);
        try {
            const r = await dados().gerarFaturas(competencia);
            aviso({
                title: r.criadas ? `${r.criadas} ${r.criadas === 1 ? 'fatura criada' : 'faturas criadas'}` : 'Nada a gerar',
                description: r.criadas
                    ? `competência ${competencia} · descontos e créditos já abatidos`
                    : 'as clínicas que pagam já têm fatura desta competência',
                tone: r.criadas ? 'success' : 'info',
            });
            if (r.criadas && pode('faturamento.gerenciar')) await cobrarPendentes(false);
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível gerar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setGerando(false);
        }
    };

    const [confirmar, confirmacao] = useConfirmacao();
    const cobranca = useCobrancaDasFaturas(confirmar);

    const baixar = async (f: Fatura) => {
        if (f.pagoEm) {
            const ok = await confirmar({
                titulo: `Desfazer a baixa da fatura de ${f.clinica}?`,
                descricao: `A competência ${f.competencia} volta a constar como em aberto.`,
                confirmar: 'Desfazer baixa',
            });
            if (!ok) return;
        }
        try {
            const hoje = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
            await dados().baixarFatura(f.id, f.pagoEm ? null : hoje);
            aviso({ title: f.pagoEm ? 'Baixa desfeita' : 'Fatura baixada', description: `${f.clinica} · ${f.competencia}` });
            revalidator.revalidate();
        } catch (e) {
            aviso({ title: 'Não foi possível registrar', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        }
    };

    const colunas = colunasDeFatura({ onBaixar: (f) => { void baixar(f); }, onEstornar: setEstornando, comClinica: true, ...cobranca });

    const vencidas = carga.faturas.filter((f) => f.situacao === 'vencida');
    const lista = aba === 'inadimplencia' ? vencidas : carga.faturas;
    const clinicas = carga.assinaturas.map((a) => ({ id: a.clinicaId, nome: a.clinica }));

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {confirmacao}
            <PageHeader
                title="Faturamento"
                description="As faturas das assinaturas e as avulsas. O valor vem do plano — e sem preço não há o que faturar. Com um gateway ativo em Integrações, cada fatura ganha link de pagamento e o aviso do gateway dá a baixa; sem gateway, a baixa é registrada aqui."
                actions={(
                    <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {aba === 'faturas' && !pode('faturamento.gerenciar') && (
                            <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                                Gerar e emitir fatura pede a permissão “Gerar faturas e emitir fatura avulsa”.
                            </span>
                        )}
                        {aba === 'faturas' && carga.temPreco && pode('faturamento.gerenciar') && (
                            <>
                                <input
                                    type="month" value={competencia} onChange={(e) => setCompetencia(e.target.value)}
                                    aria-label="Competência a gerar"
                                    style={{ height: 36, padding: '0 10px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-input)', fontFamily: 'var(--font-ui)', fontSize: 13 }}
                                />
                                <Button onClick={() => { void gerar(); }} disabled={gerando} iconLeft={<Icon name="receipt" size={15} />}>
                                    {gerando ? 'Gerando…' : 'Gerar competência'}
                                </Button>
                            </>
                        )}
                        {pode('faturamento.gerenciar') && (
                            <Button variant="secondary" iconLeft={<Icon name="plus" size={15} />} onClick={() => setAvulsa(true)}>Fatura avulsa</Button>
                        )}
                        {aba === 'faturas' && pode('faturamento.gerenciar') && (
                            <Button variant="secondary" iconLeft={<Icon name="link" size={15} />} onClick={() => { void cobrarPendentes(true); }} disabled={cobrando}>
                                {cobrando ? 'Cobrando…' : 'Cobrar pendentes'}
                            </Button>
                        )}
                    </span>
                )}
            >
                <Tabs
                    ariaLabel="Seções do faturamento" activeId={aba}
                    onChange={(id) => navigate(`/admin/faturamento/${id}`)}
                    tabs={(rota?.children ?? []).map((c) => ({
                        id: c.path,
                        label: c.label,
                        count: c.path === 'faturas' ? carga.faturas.length : c.path === 'inadimplencia' ? vencidas.length : undefined,
                    }))}
                />
            </PageHeader>

            {!carga.temPreco && (
                <Alert tone="warning" dismissible={false} title="Nenhum plano tem preço definido">
                    Sem preço não há mensalidade para gerar nem receita para somar.{' '}
                    <Link to="/admin/planos" style={{ color: 'var(--text-brand)', fontWeight: 600 }}>
                        Definir os preços em Planos
                    </Link>{' '}
                    liga esta tela. Fatura avulsa não depende de preço.
                </Alert>
            )}

            {aba === 'resumo' && (
                <>
                    <div className="vec-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: 12 }}>
                        {carga.indicadores.map((i) => (
                            <KpiCard key={i.id} label={i.rotulo} value={i.valor} hint={i.apoio} deltaTone={i.tendencia} icon={<Icon name={i.icone} size={16} />} />
                        ))}
                    </div>

                    <Card title="Assinaturas" subtitle="Uma por clínica, apontando para um plano" padding={0}>
                        <DataTable
                            columns={[
                                {
                                    key: 'clinica', header: 'Clínica',
                                    render: (a) => (
                                        <Link to={`/admin/clinicas/${a.clinicaId}/assinatura`} style={{ color: 'var(--text-primary)', fontWeight: 600, textDecoration: 'none' }}>
                                            {a.clinica}
                                        </Link>
                                    ),
                                },
                                { key: 'plano', header: 'Plano', width: 150 },
                                { key: 'situacao', header: 'Situação', width: 140 },
                                { key: 'inicio', header: 'Desde', width: 130, render: (a) => dataSimples(a.inicio.slice(0, 10)) },
                                {
                                    key: 'precoCentavos', header: 'Valor', align: 'right', width: 130,
                                    render: (a) => (a.precoCentavos === undefined ? <span style={{ color: 'var(--warning-strong)' }}>a definir</span> : moeda(a.precoCentavos)),
                                },
                            ]}
                            rows={carga.assinaturas} rowKey="clinicaId"
                            stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                        />
                    </Card>
                </>
            )}

            {(aba === 'faturas' || aba === 'inadimplencia') && (
                <Card padding={0}>
                    <DataTable
                        columns={colunas} rows={lista} rowKey="id"
                        stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                        emptyMessage={aba === 'inadimplencia' ? 'Nenhuma fatura vencida.' : 'Nenhuma fatura registrada.'}
                    />
                </Card>
            )}

            {/* A nota da mensalidade (0176), emitida pelo Focus NFe (0177) depois
                que a empresa da plataforma é ligada ao emissor. */}
            {aba === 'notas' && (
                <PainelDeNotasFiscais
                    alvo="plataforma"
                    faturasPagas={carga.faturas.filter((f) => f.situacao === 'paga')
                        .map((f) => ({ id: f.id, clinica: f.clinica, competencia: f.competencia, valorCentavos: f.valorCentavos }))}
                />
            )}

            <FaturaAvulsaModal aberto={avulsa} onFechar={() => setAvulsa(false)} clinicas={clinicas} />
            <EstornoModal fatura={estornando} onFechar={() => setEstornando(null)} />
        </div>
    );
}
